import type { UploadItem } from './uploads';

// The question after an upload (TF-461): which batches of files have settled and wait for a choice
// about their details, apart from the panel that shows it.

export interface BatchPrompt {
  tenantId: string;
  batchId: string;
  /** The files of the batch that made it. */
  count: number;
  /** Where the upload was started: an upload onto a record files its task there. */
  tag: string | null;
}

const SETTLED: readonly UploadItem['status'][] = ['done', 'blocked', 'failed'];

/** Each batch once, when every file of it has settled and one or more came through as a file of its own. */
export function batchPrompts(items: readonly UploadItem[], answered: ReadonlySet<string>): BatchPrompt[] {
  const batches = new Map<string, UploadItem[]>();
  for (const item of items) {
    if (item.uploadBatchId && !answered.has(item.uploadBatchId)) batches.set(item.uploadBatchId, [...(batches.get(item.uploadBatchId) ?? []), item]);
  }
  const prompts: BatchPrompt[] = [];
  for (const [batchId, files] of batches) {
    if (!files.every((file) => SETTLED.includes(file.status))) continue;
    const through = files.filter((file) => file.status === 'done' && file.isNew);
    if (through.length > 0) prompts.push({ tenantId: files[0]!.tenantId, batchId, count: through.length, tag: files[0]!.tag });
  }
  return prompts;
}

const UUID = '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}';
const RECORD_TAG = new RegExp(`^crm:(deal|account|contact):(${UUID})$`, 'i');

/** The record an upload's tag names ("crm:deal:<id>"), as the choice takes it. */
export function recordOfTag(tag: string | null): { dealId?: string; accountId?: string; contactId?: string } {
  const match = tag ? RECORD_TAG.exec(tag) : null;
  if (!match) return {};
  return { [`${match[1]!.toLowerCase()}Id`]: match[2]! };
}
